/**
 * Application Type Definitions
 *
 * TypeScript types for application entities and operations.
 * Mirrors backend models from tenant-api/src/models/index.ts
 */

import type { RoleAssignment } from './role.types';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Application deployment status
 */
export const ApplicationDeploymentStatus = {
  PENDING: 'pending',
  DEPLOYING: 'deploying',
  DEPLOYED: 'deployed',
  FAILED: 'failed',
} as const;

export type ApplicationDeploymentStatus = typeof ApplicationDeploymentStatus[keyof typeof ApplicationDeploymentStatus];

/**
 * Execution mode for deployments
 * - LATEST: Application automatically runs the most recently deployed code
 * - CUSTOM: Application runs a specific deployment selected manually
 */
export const ExecutionMode = {
  LATEST: 'latest',
  CUSTOM: 'custom',
} as const;

export type ExecutionMode = typeof ExecutionMode[keyof typeof ExecutionMode];

// ============================================================================
// ENVIRONMENT VARIABLE
// ============================================================================

/**
 * Environment variable for applications
 * Key must be uppercase with letters, numbers, and underscores only
 */
export interface EnvironmentVariable {
  key: string; // ^[A-Z][A-Z0-9_]*$ - Uppercase, letters/numbers/underscores
  value: string; // Any string value (max 4096 characters)
}

// ============================================================================
// APPLICATION ENTITY
// ============================================================================

/**
 * Application - Custom applications
 */
export interface Application {
  tenantId: string;
  applicationId: string; // UUID v4
  applicationName: string;
  description?: string;
  deploymentStatus: ApplicationDeploymentStatus;

  // Application identification and permissions
  applicationCode: string; // Unique code (e.g., "inventory", "sales") - immutable after creation
  actions: string[]; // Supported actions (e.g., ["read", "write", "create", "update", "delete"])

  // Activation control
  isActive: boolean; // Controls whether application can be launched/executed
  showInLauncher: boolean; // Controls visibility in launcher UI (backend-only apps may not need UI)

  // Menu organization
  menu?: string; // e.g., "Analytics", "CRM", "Finance"
  submenu?: string; // e.g., "Reports", "Dashboards"

  // Environment variables (key/value pairs for runtime configuration)
  environmentVariables?: EnvironmentVariable[];

  // Role - APPLICATION role defining Ketrics resource permissions
  // (volumes, secrets, connections, etc.) granted to this application
  role?: RoleAssignment;

  // Deployment reference
  currentDeploymentId?: string; // Active deployment (references Deployment entity)
  executionMode?: ExecutionMode; // How the application consumes deployments

  // Deployment details
  functionArn?: string; // Lambda ARN
  apiEndpoint?: string;
  codeBundle?: string; // S3 path to code bundle

  // Metadata
  createdBy: string; // User ID
  deployedAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Sanitized application (for API responses)
 */
export type ApplicationSanitized = Application;

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for application creation
 */
export interface ApplicationCreateDTO {
  applicationName: string;
  applicationCode: string; // Unique code (lowercase alphanumeric + hyphens)
  actions: string[]; // Supported actions (e.g., ["read", "write", "delete"])
  description?: string;
  isActive?: boolean;
  showInLauncher?: boolean; // Default: true - controls visibility in launcher
  menu?: string;
  submenu?: string;
  environmentVariables?: EnvironmentVariable[];
  roleId?: string; // APPLICATION role for Ketrics resource permissions
}

/**
 * DTO for application updates
 * Note: applicationCode is immutable and cannot be updated
 */
export interface ApplicationUpdateDTO {
  applicationName?: string;
  actions?: string[]; // Update supported actions
  description?: string;
  functionArn?: string;
  apiEndpoint?: string;
  codeBundle?: string;
  deployedAt?: string;
  isActive?: boolean;
  showInLauncher?: boolean; // Controls visibility in launcher UI
  menu?: string;
  submenu?: string;
  currentDeploymentId?: string;
  executionMode?: ExecutionMode; // How the application consumes deployments
  environmentVariables?: EnvironmentVariable[];
  roleId?: string | null; // APPLICATION role for Ketrics resource permissions (null to remove)
}

/**
 * DTO for application deployment
 */
export interface ApplicationDeploymentDTO {
  codeBundle: string;
  environmentVariables?: Record<string, string>;
}

/**
 * DTO for code upload metadata
 */
export interface ApplicationCodeUploadDTO {
  fileName: string;
  fileSize: number;
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * Application list query parameters
 */
export interface ApplicationListQuery {
  status?: ApplicationDeploymentStatus;
  search?: string;
  limit?: number;
}

/**
 * Application logs query parameters
 */
export interface ApplicationLogsQuery {
  startTime?: number;
  endTime?: number;
  limit?: number;
  searchTerm?: string;
}

// ============================================================================
// LOG TYPES
// ============================================================================

/**
 * Application log entry
 */
export interface ApplicationLogEntry {
  timestamp: string;
  message: string;
  level?: string;
  requestId?: string;
  functionName?: string;
}

/**
 * Application logs response
 */
export interface ApplicationLogsResponse {
  logs: ApplicationLogEntry[];
  nextToken?: string;
}
